/* eslint-disable no-nested-ternary */
import React from 'react';

import { useNavigate, useParams, Link } from 'react-router-dom';

import Table from '../Table';
import Reverse from './Reverse';

import generateListContent from '../../helpers/generateListContent';
import sumDollarAmounts from '../../helpers/sumDollarAmounts';
// import { formatCurrency } from '../../helpers/currencyMask';

function generateBillContent(bill, type, navigate, createPaymentParts) {
  let currentParts;

  if (createPaymentParts) {
    currentParts = createPaymentParts(bill);
  }

  const billPaymentParts = [
    {
      title: 'Bill Number',
      field: 'pixel_bill_number',
      link: `/loans/${bill._loan?.id}/bills/${bill.id}?tab=Bill`,
    },
    { title: 'Status', field: 'status' },
    { title: 'Due Date', field: 'due_date' },
    /* { title: 'Late Date', field: 'late_date' }, */
    {
      title: bill._vendor ? 'Vendor' : 'Lender',
      field: bill._vendor ? '_vendor.name' : '_lender.name',
    },
    { title: 'Type', field: 'type' },
    { title: 'Payee Type', field: 'payee_type' },
    {
      title: 'Payee',
      field: 'payee_type',
      format: (value, row) => {
        if (row._borrower) {
          return value?.TIN_type === 'EIN'
            ? row._borrower?.entity_name
            : `${row._borrower?.first_name || ''} ${
                row._borrower?.last_name || ''
              }`;
        }
        if (row._vendor) {
          return row._vendor?.name;
        }
        if (row._lender) {
          return row._lender?.name;
        }
        return null;
      },
    },
    {
      title: 'Loan',
      field: '_loan._property.address_line_1',
      link: `/loans/${bill._loan?.id}/bill-payments/${bill.bill_payment_id}?tab=Bill Payment`,
    },
    {
      title: 'Borrower',
      field: '_loan._borrower_primary',
      format: (value) =>
        value?.TIN_type === 'EIN'
          ? value?.entity_name
          : `${value?.first_name || ''} ${value?.last_name || ''}`,
    },
  ];

  const transferBillParts = [
    {
      title: 'Date',
      field: 'date',
    },
    {
      title: 'Loan',
      field: '_loan.pixel_loan_number',
      link: `/loans/${bill._loan?.id}?tab=Loan`,
    },
    { title: 'Status', field: 'status' },
    { title: 'Type', field: 'type' },
    { title: 'Bill.com ID', field: 'billcom_id' },
  ];

  if (!currentParts) {
    currentParts =
      type === 'BillPayment' ? billPaymentParts : transferBillParts;
  }

  return generateListContent(currentParts, bill, navigate);
}

export default function Bill({
  bill,
  billLineItemIds,
  setBillLineItemIds,
  type = 'BillPayment',
  createPaymentParts,
  lineItems,
  lineItemsCols,
  billPaymentSentpayId,
  refetch,
}) {
  // const { showDialogModal, showToast } = useUiContext();

  const navigate = useNavigate();

  const { id: transferId } = useParams();

  const isBillPayment = type === 'BillPayment';

  const defaultCols = [
    {
      title: 'Description',
      keys: ['description'],
    },
    {
      title: 'Amount',
      keys: ['amount'],
      type: 'currency',
    },
    {
      title: isBillPayment ? 'Amount Applied' : 'Amount Transferred',
      keys: isBillPayment ? ['_amount_paid.total'] : ['amount_paid.total'],
      type: 'currency',
    },
    {
      title: 'Status',
      keys: ['status'],
    },
    {
      title: isBillPayment ? 'Invoice' : '',
      keys: ['_invoice.pixel_invoice_number'],
      format: (value) => {
        if (!isBillPayment) {
          return null;
        }

        return value;
      },
      link: (row) =>
        row._invoice
          ? `/loans/${row._invoice?.loan_id}/invoices/${row._invoice?.id}?tab=Invoice`
          : null,
    },
    {
      title: isBillPayment ? 'Add to Bill Payment' : 'Add to Transfer',
      type: 'checkbox',
      keys: ['doesntMatter'],
      isHidden: (row) => row.description === 'Total',
      disabled: (row) =>
        (isBillPayment ? row.status === 'paid' : row.status === 'complete') ||
        !row.status,
      isSelected: (row) =>
        billLineItemIds?.includes(row.id) ||
        (isBillPayment ? row.status === 'paid' : row.status === 'complete'),
      onChange: (row) => {
        if (isBillPayment ? row.status === 'paid' : row.status === 'complete') {
          return;
        }

        setBillLineItemIds((prev) => {
          if (billLineItemIds.includes(row.id)) {
            return prev.filter((id) => id !== row.id);
          }
          return [...prev, row.id];
        });
      },
      // If isBillPayment is false, and row.status is 'complete',
      // check if transfer_line_item is assosicated to the current transfer.
      // If not, return list of links to assosiated transfers instead of checkbox.
      format: (value, row) => {
        if (!isBillPayment && row.status === 'complete' && row._transfers) {
          const isAssosiatedWithCurrentTransfer = row._transfers.some(
            (transfer) => transfer.id === +transferId
          );

          if (isAssosiatedWithCurrentTransfer) {
            return false;
          }

          // Render links to transfers as comma separated list
          const pixelNumbers = row._transfers
            .filter((vl) => !!vl)
            .map((vl) => (
              <Link
                className="text-indigo-500 underline"
                to={`/transfers/${vl.id}`}
              >
                {vl.pixel_number || vl.id}
              </Link>
            ))
            .reduce((prev, curr) => (prev ? [prev, ', ', curr] : [curr]), '');

          return pixelNumbers;
        }

        return false;
      },
    },
  ];

  const reverseLineItemCol = {
    title: 'Reverse',
    keys: ['actions'],
    type: 'component',
    component: Reverse,
    props: (row) => ({ row, refetch }),
  };

  let currentLineItems = lineItems;

  if (!lineItems) {
    currentLineItems =
      type === 'BillPayment'
        ? bill._bill_line_items
        : bill._transfer_bill_line_items;
  }

  let cols =
    isBillPayment && !billPaymentSentpayId
      ? defaultCols
          .map((col) => {
            if (col.title === 'Amount Applied') {
              return [col, reverseLineItemCol];
            }
            return col;
          })
          ?.flat()
      : defaultCols;

  // If there are paid line items and no billPaymentSentpayId, remove the 'Add to Bill Payment' column
  if (
    currentLineItems?.some((item) => item.status === 'paid') &&
    isBillPayment &&
    !billPaymentSentpayId
  ) {
    cols = cols.filter((col) => col.title !== 'Add to Bill Payment');
  }

  const lineItemsWithTotal = [
    ...(currentLineItems || []),
    {
      description: 'Total',
      amount: sumDollarAmounts(currentLineItems, 'amount'),
      _amount_paid: {
        total: sumDollarAmounts(currentLineItems, '_amount_paid.total'),
      },
      amount_paid: {
        total: sumDollarAmounts(currentLineItems, 'amount_paid.total'),
      },
      status: '',
      style: 'font-semibold',
    },
  ];

  return (
    <div>
      <div className="flex flex-col md:flex-row gap-4 flex-wrap mb-4">
        {generateBillContent(bill, type, navigate, createPaymentParts)}
      </div>
      <Table
        title="Line Items"
        rows={lineItems || lineItemsWithTotal}
        cols={lineItemsCols || cols}
      />
    </div>
  );
}
